import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreationsPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [creations, setCreations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
    fetchCreations();
  }, []);

  const fetchCreations = async () => {
    try {
      const response = await axios.get(`${API}/creations`);
      // Map backend fields to frontend expected fields
      const mappedCreations = response.data.map(creation => ({
        id: creation.id,
        title: creation.title,
        category: creation.category,
        image: creation.image_url,
        description: creation.description
      }));
      setCreations(mappedCreations);
    } catch (error) {
      console.error('Error fetching creations:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'Toutes' },
    { id: 'bouquets', name: 'Bouquets' },
    { id: 'evenements', name: 'Événements' },
    { id: 'mariages', name: 'Mariages' },
  ];

  const filteredCreations = selectedCategory === 'all'
    ? creations
    : creations.filter(c => c.category === selectedCategory);

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#E8A5C4]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#C45B9F]/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div
            className={`text-center max-w-3xl mx-auto transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="text-[#C45B9F] font-medium uppercase tracking-wider text-sm">
              Nos réalisations
            </span>
            <h1 className="font-heading text-5xl lg:text-6xl text-[#3D2E35] mt-4">
              Créations <span className="font-script text-[#8B4B7C]">Florales</span>
            </h1>
            <p className="text-lg text-[#6B5A62] mt-6">
              Chaque composition est une œuvre unique, inspirée par la beauté des tropiques
            </p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex flex-wrap justify-center gap-4 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-[#8B4B7C] text-white shadow-lg shadow-[#8B4B7C]/30'
                    : 'bg-white text-[#6B5A62] hover:bg-[#E8A5C4]/20 border border-[#E8A5C4]/30'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-10 h-10 border-4 border-[#8B4B7C] border-t-transparent rounded-full"></div>
            </div>
          ) : creations.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#6B5A62] text-lg">Aucune création disponible pour le moment.</p>
              <p className="text-[#C45B9F] mt-2">Revenez bientôt pour découvrir nos nouvelles réalisations !</p>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCreations.map((creation, index) => (
              <div
                key={creation.id}
                onClick={() => setSelectedImage(creation)}
                className={`group card-tevaite overflow-hidden cursor-pointer transition-all duration-700 ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="img-hover-zoom aspect-[4/5] relative">
                  <img
                    src={creation.image}
                    alt={creation.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#3D2E35]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <span className="text-[#E8A5C4] text-sm uppercase tracking-wider">
                      {creation.category}
                    </span>
                    <h3 className="text-white font-heading text-xl mt-1">
                      {creation.title}
                    </h3>
                  </div>
                </div>
                <div className="p-5">
                  <span className="text-[#C45B9F] text-xs uppercase tracking-wider">
                    {creation.category}
                  </span>
                  <h3 className="font-heading text-lg text-[#3D2E35] mt-1 group-hover:text-[#8B4B7C] transition-colors duration-300">
                    {creation.title}
                  </h3>
                  <p className="text-[#6B5A62] text-sm mt-2">
                    {creation.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl w-full animate-bloom">
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[85vh] object-contain rounded-2xl"
            />
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent rounded-b-2xl">
              <span className="text-[#E8A5C4] text-sm uppercase tracking-wider">
                {selectedImage.category}
              </span>
              <h3 className="text-white font-heading text-2xl mt-1">
                {selectedImage.title}
              </h3>
              <p className="text-white/80 mt-2">
                {selectedImage.description}
              </p>
            </div>
            <button
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CreationsPage;
